import React, { useContext } from "react";
import { FaPlay, FaEdit, FaTrash, FaPlus } from "react-icons/fa";
import { HashLoader } from "react-spinners";
import { Prompt } from "./types";
import { GoogleAnalyticsContext } from "../../../../Hooks/GoogleAnalyticsContext";
import { Link } from "react-router-dom";

interface PromptListProps {
  prompts: Prompt[];
  isLoading: boolean;
  isDeleting: boolean;
  onEdit: (prompt: Prompt) => void;
  onDelete: (prompt: Prompt) => void;
  onUse: (prompt: Prompt) => void;
}

const PromptList: React.FC<PromptListProps> = ({
  prompts,
  isLoading,
  isDeleting,
  onEdit,
  onDelete,
  onUse,
}) => {
  const { logEvent } = useContext(GoogleAnalyticsContext);

  if (isLoading) {
    logEvent("PromptList", "Loading");
    return (
      <div className="bg-white rounded-lg shadow-md p-6 flex justify-center items-center h-64">
        <HashLoader size={30} color="#FFC60B" />
      </div>
    );
  }

  if (prompts.filter((p) => !p.isSystem).length === 0) {
    logEvent("PromptList", "Empty");
    return (
      <div className="bg-white rounded-lg shadow-md p-8 text-center">
        <h3 className="font-semibold text-xl text-gray-800 mb-2">
          No custom prompts yet
        </h3>
        <p className="text-gray-600 mb-4">
          Create your first custom prompt to build your own AI-powered tools
        </p>
        <p className="text-gray-600 mb-6 max-w-lg mx-auto">
          With custom prompts, you can create specialized tools for analyzing
          texts, generating content, creating assessments, and much more - all
          tailored to your specific needs.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      {prompts
        .filter((p) => !p.isSystem)
        .map((prompt) => (
          <div
            key={prompt.id}
            className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
          >
            <div className="p-5">
              <h3 className="font-semibold text-lg text-gray-800 mb-2">
                {prompt.title}
              </h3>
              <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                {prompt.promptText.length > 150
                  ? prompt.promptText.substring(0, 150) + "..."
                  : prompt.promptText}
              </p>
            </div>

            <div className="bg-gray-50 px-5 py-3 border-t border-gray-100 flex justify-between">
              <div className="text-sm text-gray-500">
                Created: {new Date(prompt.createdAt).toLocaleDateString()}
              </div>
              <div className="flex space-x-2">
                <button
                  onClick={() => {
                    logEvent(
                      "PromptAction",
                      "UseFromList",
                      `prompt_${prompt.id}`
                    );
                    onUse(prompt);
                  }}
                  className="p-1.5 bg-green-100 text-green-700 rounded hover:bg-green-200"
                  title="Use Prompt"
                >
                  <FaPlay size={14} />
                </button>
                <button
                  onClick={() => {
                    logEvent(
                      "PromptAction",
                      "EditFromList",
                      `prompt_${prompt.id}`
                    );
                    onEdit(prompt);
                  }}
                  className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  title="Edit Prompt"
                >
                  <FaEdit size={14} />
                </button>
                <button
                  onClick={() => {
                    logEvent(
                      "PromptAction",
                      "DeleteFromList",
                      `prompt_${prompt.id}`
                    );
                    onDelete(prompt);
                  }}
                  className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"
                  title="Delete Prompt"
                  disabled={isDeleting}
                >
                  {isDeleting ? (
                    <HashLoader size={14} color="#DC2626" />
                  ) : (
                    <FaTrash size={14} />
                  )}
                </button>
              </div>
            </div>
          </div>
        ))}
    </div>
  );
};

export default PromptList;
